import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Flex } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import './FilterDropdowns.css';
import { FilterDropdownsProps } from '../ControlTower/types';
import { AlertFilterIcon, ChevronDownIcon } from '@/assets/icons/icons';

const PILL_OFFSET_Y = -4;
const SUBMENU_VIEWPORT_BUFFER = 16;
const SUBMENU_MIN_HEIGHT = 240;
const SUBMENU_MAX_HEIGHT = 550;

const FilterDropdowns = ({
  addFilterFontSize,
  addFilterBgColor,
  openedFromPill,
  submenuAnchor,
  appliedFilters = [],
  menu,
  isFilterOpen,
  setIsFilterOpen,
  secondFilterDisplay,
  setSecondFilterDisplay,
  filterBy,
  isDatePicker,
  calendarMenu,
  renderSubMenu,
  zIndex = true,
  showAddFilter = true,
  isAiHistory = false,
  getPopupContainer,
  popupClassName,
  pillsDropdownMenuItems,
  isShipmentToolbarFilter,
}: FilterDropdownsProps) => {
  const addFilterRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [anchor, setAnchor] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const [availableHeight, setAvailableHeight] = useState<number | undefined>(
    undefined
  );

  const computeAvailableHeight = (viewportTop: number): number => {
    const space = window.innerHeight - viewportTop - SUBMENU_VIEWPORT_BUFFER;
    return Math.max(
      SUBMENU_MIN_HEIGHT,
      Math.min(SUBMENU_MAX_HEIGHT, Math.floor(space))
    );
  };

  const resolvePopupContainer = () => {
    if (getPopupContainer) {
      return getPopupContainer();
    }
    return document.body;
  };

  const toLocalCoords = (vpLeft: number, vpTop: number) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return { left: vpLeft, top: vpTop };
    }
    return {
      left: Math.round(vpLeft - containerRect.left),
      top: Math.round(vpTop - containerRect.top),
    };
  };

  useLayoutEffect(() => {
    if (!secondFilterDisplay && !isFilterOpen) return;
    if (
      openedFromPill &&
      submenuAnchor?.left != null &&
      submenuAnchor?.top != null
    ) {
      const vpTop = submenuAnchor.top + PILL_OFFSET_Y;
      const local = toLocalCoords(submenuAnchor.left, vpTop);
      setAnchor(local);
      setAvailableHeight(computeAvailableHeight(vpTop));
      return;
    }

    const r = addFilterRef.current?.getBoundingClientRect();
    if (r) {
      const local = toLocalCoords(r.left, r.bottom);
      setAnchor(local);
      setAvailableHeight(computeAvailableHeight(r.bottom));
    }
  }, [secondFilterDisplay, isFilterOpen, openedFromPill, submenuAnchor]);

  useEffect(() => {
    if (!secondFilterDisplay) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      const container = containerRef.current;
      if (container && container.contains(target)) return;
      setSecondFilterDisplay(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [secondFilterDisplay, setSecondFilterDisplay]);
  useEffect(() => {
    if (!secondFilterDisplay) return;
    if (!openedFromPill) return;
    if (!submenuAnchor?.el) return;

    const handleScroll = () => {
      const el = submenuAnchor.el;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vpTop = r.bottom + PILL_OFFSET_Y;
      const local = toLocalCoords(r.left, vpTop);
      setAnchor(local);
      setAvailableHeight(computeAvailableHeight(vpTop));
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [secondFilterDisplay, openedFromPill, submenuAnchor]);

  const circleIconContainerStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'var(--light-gray)',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div ref={containerRef} className='custom-filter-dropdown'>
      <Dropdown
        overlay={menu}
        trigger={['click']}
        autoAdjustOverflow={true}
        open={isFilterOpen}
        overlayClassName={popupClassName}
        overlayStyle={{ zIndex: zIndex ? 2000 : undefined }}
        getPopupContainer={resolvePopupContainer}
        onOpenChange={(visible: boolean) => {
          if (visible) setSecondFilterDisplay(false);
          setIsFilterOpen(visible);
        }}
      >
        {showAddFilter ? (
          <Flex
            ref={addFilterRef}
            className={`custom-filter-trigger if-shipment-filter ${
              appliedFilters.length > 0
                ? 'has-filters'
                : isAiHistory
                  ? 'no-filters-clr'
                  : 'no-filters'
            }`}
            style={{
              background:
                appliedFilters.length > 0 ? undefined : addFilterBgColor,
            }}
          >
            <AlertFilterIcon
              width={12}
              height={12}
              color={
                appliedFilters.length > 0
                  ? 'var(--surface-base)'
                  : 'var(--text-primary)'
              }
            />
            <p
              className={
                addFilterFontSize +
                ` ${!isShipmentToolbarFilter && 'add-filter'}`
              }
              // eslint-disable-next-line react/forbid-dom-props -- the font size comes from the caller's addFilterFontSize prop
              style={{ fontSize: addFilterFontSize, textWrap: 'nowrap' }}
            >
              Add filter
            </p>
            {appliedFilters?.length > 0 && isShipmentToolbarFilter && (
              <span className={addFilterFontSize + ' ' + `sm-d-block`}>
                ({appliedFilters?.length})
              </span>
            )}

            {appliedFilters.length > 0 && isShipmentToolbarFilter && (
              <Dropdown
                trigger={['click']}
                placement='bottomLeft'
                menu={{
                  items: pillsDropdownMenuItems,
                }}
                // getPopupContainer={getPopupContainer}
              >
                <Flex
                  align='center'
                  className='xl-d-none'
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  |
                  <ChevronDownIcon size={16} color='#fff' />
                </Flex>
              </Dropdown>
            )}
          </Flex>
        ) : (
          <Flex
            ref={addFilterRef}
            className={`filter-icon-circle${
              appliedFilters.length > 0 ? ' has-filters' : ''
            }`}
            style={{
              ...circleIconContainerStyle,
              // '#F3F3F3' stayed a flat light grey in dark mode too (circleIconContainerStyle's
              // own default, var(--light-gray), is already dark-mode-safe -- only this override
              // for the no-filters state wasn't). The has-filters case keeps its literal black
              // fill, so its icon is a fixed white in both themes.
              background:
                appliedFilters.length > 0 ? '#000' : 'var(--light-gray)',
            }}
          >
            <AlertFilterIcon
              color={appliedFilters.length > 0 ? '#fff' : 'var(--text-primary)'}
            />
          </Flex>
        )}
      </Dropdown>

      {secondFilterDisplay && (
        <Dropdown
          open={secondFilterDisplay}
          trigger={['click']}
          autoAdjustOverflow={true}
          overlayClassName={`subfilter-popover${
            popupClassName ? ` ${popupClassName}` : ''
          }`}
          // Keep the popup mounted while open so filter switches update
          // content in place instead of tearing down the overlay (table flash).
          destroyPopupOnHide={false}
          overlayStyle={{ zIndex: zIndex ? 2000 : undefined }}
          getPopupContainer={resolvePopupContainer}
          overlay={
            <div
              key={filterBy || 'none'}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {isDatePicker
                ? calendarMenu({
                    hideBackArrow: openedFromPill,
                    maxHeight: availableHeight,
                  })
                : renderSubMenu({
                    hideBackArrow: openedFromPill,
                    maxHeight: availableHeight,
                  })}
            </div>
          }
          placement={window.innerWidth < 500 ? 'bottomCenter' : 'bottomLeft'}
          align={window.innerWidth < 500 ? { offset: [0, 8] } : undefined}
        >
          <div
            className='subfilter-anchor'
            // eslint-disable-next-line react/forbid-dom-props -- the anchor follows the clicked pill, measured at run time
            style={{ left: anchor.left, top: anchor.top }}
          />
        </Dropdown>
      )}
    </div>
  );
};

export default FilterDropdowns;
